import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { API_URL } from '../lib/utils';
import { toast } from 'sonner';
import {
  Check,
  Crown,
  Star,
  Zap,
  Shield,
  Loader2,
  CreditCard,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  X,
} from 'lucide-react';

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

// PayPal Icon SVG Component
const PayPalIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.59 3.025-2.566 6.082-8.558 6.082h-2.19a1.594 1.594 0 0 0-1.577 1.353l-1.12 7.106-.317 2.008a.641.641 0 0 0 .633.74h3.571c.525 0 .97-.381 1.05-.9l.034-.176.66-4.183.043-.23a1.047 1.047 0 0 1 1.035-.888h.65c4.225 0 7.532-1.715 8.5-6.678.404-2.07.194-3.798-.766-5.015z"/>
  </svg>
);

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isEmployer } = useAuth();
  const [plans, setPlans] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    if (!isEmployer) {
      toast.error('Only employers can access subscriptions');
      navigate('/');
      return;
    }
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmployer]);

  const fetchData = async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        axios.get(`${API_URL}/subscriptions/plans`),
        axios.get(`${API_URL}/subscriptions/my-subscription`, {
          headers: {  },
        }).catch(() => ({ data: null })),
      ]);
      setPlans(plansRes.data);
      setCurrentSubscription(subRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const openPaymentModal = (planId) => {
    if (!user) {
      navigate('/login');
      return;
    }
    setSelectedPlan(planId);
    setPaymentMethod(null);
    setShowPaymentModal(true);
  };

  const handleStripeCheckout = async () => {
    setProcessingPlan(selectedPlan);
    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(
        `${API_URL}/subscriptions/checkout/stripe`,
        { plan_id: selectedPlan, origin_url: originUrl },
        { headers: {  } }
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setProcessingPlan(null);
    }
  };

  const createPayPalOrder = async () => {
    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(
        `${API_URL}/subscriptions/checkout/paypal`,
        { plan_id: selectedPlan, origin_url: originUrl },
        { headers: {  } }
      );
      
      // Extract order ID from approval URL
      const url = new URL(response.data.url);
      const orderToken = url.searchParams.get('token');
      return orderToken;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create PayPal order');
      throw error;
    }
  };

  const onPayPalApprove = async (data) => {
    try {
      
      const response = await axios.post(
        `${API_URL}/subscriptions/paypal/execute`,
        { paymentId: data.paymentID, PayerID: data.payerID },
        { headers: {  } }
      );
      
      if (response.data.status === 'success' || response.data.status === 'already_paid') {
        toast.success('Payment successful!');
        navigate('/employer/subscription/success?paypal=true');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const planOrder = ['monthly', '6month', 'yearly'];
  const planIcons = {
    monthly: Zap,
    '6month': Star,
    yearly: Crown,
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back button */}
        <button
          onClick={() => navigate('/employer/dashboard')}
          className="flex items-center gap-2 text-gray-600 hover:text-primary mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* From Post Job Banner */}
        {searchParams.get('from') === 'post-job' && (
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl p-6 mb-8">
            <div className="flex items-center gap-4">
              <CheckCircle2 className="w-8 h-8 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-lg">Your Job Has Been Posted!</h3>
                <p className="text-white/90 text-sm">
                  Subscribe now to view applicant details, contact candidates directly, and unlock all features.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-secondary/10 text-secondary mb-4">
            <Shield className="w-3 h-3 mr-1" /> Employer Memberships
          </Badge>
          <h1 className="font-serif text-4xl font-bold text-gray-900 mb-4">
            Choose Your Hiring Plan
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-4">
            Get access to qualified LMSW and LCSW professionals. 
            All plans include unlimited job postings.
          </p>
          {/* Flat Rate Badge */}
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full">
            <CheckCircle2 className="w-4 h-4" />
            <span className="font-semibold text-sm">Flat-Rate Employer Access — No Pay-Per-Click Confusion!</span>
          </div>
        </div>

        {/* Important Notices */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="font-semibold text-amber-800 text-sm mb-1">No Guarantees</h4>
            <p className="text-xs text-amber-700">
              Results vary based on market conditions. We do not guarantee applicant volume or hiring outcomes.
            </p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-semibold text-red-800 text-sm mb-1">Non-Refundable</h4>
            <p className="text-xs text-red-700">
              All membership fees are final and non-refundable. No credits for early cancellation.
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 text-sm mb-1">Privacy Protected</h4>
            <p className="text-xs text-blue-700">
              Your data is secure. Review our{' '}
              <a href="/privacy" className="underline">Privacy Policy</a>.
            </p>
          </div>
        </div>

        {/* Current Subscription Banner */}
        {currentSubscription && currentSubscription.status === 'active' && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mb-8">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              <div>
                <h3 className="font-semibold text-emerald-800">
                  Active Subscription: {currentSubscription.plan_name}
                </h3>
                <p className="text-sm text-emerald-600">
                  Expires: {new Date(currentSubscription.end_date).toLocaleDateString()}
                  {currentSubscription.payment_method && (
                    <span className="ml-2 capitalize">• Paid via {currentSubscription.payment_method}</span>
                  )}
                </p>
              </div>
              {currentSubscription.badge && (
                <Badge className="ml-auto bg-secondary text-white">
                  {currentSubscription.badge}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {planOrder.map((planId, index) => {
            const plan = plans?.[planId];
            if (!plan) return null;
            
            const Icon = planIcons[planId];
            const isPopular = planId === '6month';
            const isBest = planId === 'yearly';
            const isCurrentPlan = currentSubscription?.plan_id === planId && currentSubscription?.status === 'active';

            return (
              <div
                key={planId}
                className={`relative bg-white rounded-2xl border-2 transition-all ${
                  isPopular 
                    ? 'border-secondary shadow-lg scale-105' 
                    : isBest 
                    ? 'border-primary shadow-md'
                    : 'border-gray-100'
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-secondary text-white px-4 py-1">
                      Most Popular
                    </Badge>
                  </div>
                )}
                {isBest && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-white px-4 py-1">
                      Best Value
                    </Badge>
                  </div>
                )}

                <div className="p-6 lg:p-8">
                  {/* Plan Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      isPopular ? 'bg-secondary/10' : isBest ? 'bg-primary/10' : 'bg-gray-100'
                    }`}>
                      <Icon className={`w-6 h-6 ${
                        isPopular ? 'text-secondary' : isBest ? 'text-primary' : 'text-gray-600'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl font-semibold text-gray-900">
                        {plan.name}
                      </h3>
                      {plan.badge && (
                        <Badge variant="outline" className="text-xs mt-1">
                          {plan.badge}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="font-serif text-4xl font-bold text-gray-900">
                        ${plan.price.toLocaleString()}
                      </span>
                      {planId === 'monthly' && (
                        <span className="text-gray-500">/month</span>
                      )}
                    </div>
                    {plan.monthly_equivalent && (
                      <p className="text-sm text-gray-500 mt-1">
                        ${plan.monthly_equivalent}/month equivalent
                      </p>
                    )}
                    {plan.savings && (
                      <p className="text-sm text-emerald-600 font-medium mt-1">
                        Save ${plan.savings} vs monthly!
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          isPopular ? 'text-secondary' : isBest ? 'text-primary' : 'text-gray-400'
                        }`} />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button
                    onClick={() => openPaymentModal(planId)}
                    disabled={processingPlan === planId || isCurrentPlan}
                    className={`w-full h-12 ${
                      isCurrentPlan
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        : isPopular 
                        ? 'bg-secondary hover:bg-secondary-hover text-white' 
                        : isBest
                        ? 'bg-primary hover:bg-primary-hover text-white'
                        : 'bg-gray-900 hover:bg-gray-800 text-white'
                    }`}
                    data-testid={`subscribe-${planId}-btn`}
                  >
                    {processingPlan === planId ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : isCurrentPlan ? (
                      'Current Plan'
                    ) : (
                      'Subscribe Now'
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Payment Methods */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500 mb-4">Secure payments powered by</p>
          <div className="flex items-center justify-center gap-8">
            <div className="flex items-center gap-2 text-gray-500">
              <CreditCard className="w-5 h-5" />
              <span className="text-sm font-medium">Stripe</span>
            </div>
            <div className="flex items-center gap-2 text-[#003087]">
              <PayPalIcon />
              <span className="text-sm font-medium">PayPal</span>
            </div>
          </div>
        </div>

        {/* Policies Link */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/policies')}
            className="text-sm text-gray-500 hover:text-primary underline"
          >
            View Refund Policy & Privacy Policy
          </button>
        </div>
      </div>

      {/* Payment Method Modal */}
      {showPaymentModal && selectedPlan && plans?.[selectedPlan] && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowPaymentModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="font-serif text-2xl font-bold text-gray-900 mb-2">
              Choose Payment Method
            </h2>
            <p className="text-gray-600 mb-6">
              {plans[selectedPlan].name} - ${plans[selectedPlan].price.toLocaleString()}
            </p>

            <div className="space-y-3">
              {/* Stripe Option */}
              <button
                onClick={() => setPaymentMethod('stripe')}
                className={`w-full p-4 border-2 rounded-xl flex items-center gap-4 transition-all ${
                  paymentMethod === 'stripe' 
                    ? 'border-primary bg-primary/5' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="payment-method-stripe"
              >
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-gray-700" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-semibold text-gray-900">Credit/Debit Card</p>
                  <p className="text-sm text-gray-500">Visa, Mastercard, Amex</p>
                </div>
                {paymentMethod === 'stripe' && (
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                )}
              </button>

              {/* PayPal Option */}
              <button
                onClick={() => setPaymentMethod('paypal')}
                className={`w-full p-4 border-2 rounded-xl flex items-center gap-4 transition-all ${
                  paymentMethod === 'paypal' 
                    ? 'border-[#003087] bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="payment-method-paypal"
              >
                <div className="w-12 h-12 bg-[#003087]/10 rounded-lg flex items-center justify-center">
                  <PayPalIcon />
                </div>
                <div className="text-left flex-1">
                  <p className="font-semibold text-gray-900">PayPal</p>
                  <p className="text-sm text-gray-500">Pay with your PayPal account</p>
                </div>
                {paymentMethod === 'paypal' && (
                  <CheckCircle2 className="w-5 h-5 text-[#003087]" />
                )}
              </button>
            </div>

            {/* Terms Agreement */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  data-testid="terms-checkbox"
                />
                <span className="text-sm text-gray-600">
                  I have read and agree to the{' '}
                  <a href="/terms" target="_blank" className="text-primary hover:underline">Terms & Policies</a>. 
                  I understand that results are not guaranteed and all payments are non-refundable.
                </span>
              </label>
            </div>

            {/* Continue Button or PayPal Button */}
            <div className="mt-6">
              {!agreedToTerms && (
                <p className="text-center text-amber-600 text-sm mb-4">
                  Please agree to the Terms & Policies to continue
                </p>
              )}
              
              {paymentMethod === 'stripe' && (
                <Button
                  onClick={handleStripeCheckout}
                  disabled={processingPlan === selectedPlan || !agreedToTerms}
                  className="w-full h-12 bg-primary hover:bg-primary-hover text-white disabled:opacity-50"
                  data-testid="continue-stripe-btn"
                >
                  {processingPlan === selectedPlan ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Redirecting to Stripe...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Continue to Checkout
                    </>
                  )}
                </Button>
              )}

              {paymentMethod === 'paypal' && PAYPAL_CLIENT_ID && agreedToTerms && (
                <PayPalScriptProvider options={{ 
                  clientId: PAYPAL_CLIENT_ID,
                  currency: "USD",
                  disableFunding: "paylater,credit"
                }}>
                  <PayPalButtons
                    style={{ layout: "vertical", shape: "rect" }}
                    createOrder={createPayPalOrder}
                    onApprove={onPayPalApprove}
                    onError={(err) => {
                      console.error('PayPal error:', err);
                      toast.error('PayPal payment failed');
                    }}
                    onCancel={() => {
                      toast.info('Payment cancelled');
                    }}
                  />
                </PayPalScriptProvider>
              )}

              {paymentMethod === 'paypal' && !agreedToTerms && (
                <Button disabled className="w-full h-12 opacity-50">
                  Agree to Terms to Continue with PayPal
                </Button>
              )}

              {!paymentMethod && (
                <p className="text-center text-gray-500 text-sm">
                  Select a payment method to continue
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Subscription Success Page
const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [status, setStatus] = useState('checking');
  const [subscription, setSubscription] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const isPayPal = searchParams.get('paypal');
    
    if (sessionId) {
      // Stripe payment
      pollPaymentStatus(sessionId);
    } else if (isPayPal) {
      // PayPal payment - already processed, just fetch subscription
      fetchCurrentSubscription();
    } else {
      navigate('/employer/subscription');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchCurrentSubscription = async () => {
    try {
      const response = await axios.get(`${API_URL}/subscriptions/my-subscription`, {
        headers: {  },
      });
      
      if (response.data && response.data.status === 'active') {
        setStatus('success');
        setSubscription(response.data);
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempt >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    setAttempts(attempt + 1);

    try {
      const response = await axios.get(
        `${API_URL}/subscriptions/checkout/status/${sessionId}`,
        { headers: {  } }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setSubscription(response.data.subscription);
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-2xl p-8 text-center shadow-lg">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-4" />
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-2">
                Processing Payment...
              </h2>
              <p className="text-gray-600">
                Please wait while we confirm your payment.
              </p>
              <p className="text-sm text-gray-400 mt-4">
                Attempt {attempts}/10
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-2">
                Payment Successful!
              </h2>
              <p className="text-gray-600 mb-6">
                Your {subscription?.plan_name} subscription is now active.
              </p>
              {subscription?.badge && (
                <Badge className="bg-secondary text-white mb-6">
                  {subscription.badge}
                </Badge>
              )}
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-gray-500">Subscription Details:</p>
                <p className="font-medium">{subscription?.plan_name}</p>
                <p className="text-sm text-gray-600">
                  Valid until: {new Date(subscription?.end_date).toLocaleDateString()}
                </p>
              </div>
              <Button
                onClick={() => navigate('/employer/dashboard')}
                className="w-full bg-primary hover:bg-primary-hover text-white"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-amber-600" />
              </div>
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-2">
                Session Expired
              </h2>
              <p className="text-gray-600 mb-6">
                Your payment session has expired. Please try again.
              </p>
              <Button
                onClick={() => navigate('/employer/subscription')}
                className="w-full bg-primary hover:bg-primary-hover text-white"
              >
                Try Again
              </Button>
            </>
          )}

          {(status === 'error' || status === 'timeout') && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-2">
                Something Went Wrong
              </h2>
              <p className="text-gray-600 mb-6">
                We couldn't confirm your payment. Please check your email for confirmation or contact support.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/employer/subscription')}
                  className="w-full bg-primary hover:bg-primary-hover text-white"
                >
                  Back to Plans
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/employer/dashboard')}
                  className="w-full"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export { SubscriptionPlans, SubscriptionSuccess };
